import React, { useState, useEffect } from 'react';
import { useUser } from '../../contexts/UserContext';
import { supabase } from '../../utils/supabaseClient';
import {
  WrenchScrewdriverIcon,
  PlusIcon,
  TrashIcon,
  CheckCircleIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';



// Native <select> popups are drawn by the OS, so they ignore the app's dark
// glassmorphism unless told: color-scheme dark themes the popup itself, and the
// option class themes each row. Without both, the wizard's dropdowns opened as a
// bright white list against a dark form. Same pair QuoteBuilder uses.
const selectOptionClass = 'bg-slate-800 text-gray-100';

const ServicesStep = ({ onComplete, onValidationChange, quickStartMode = false }) => {
  const { user } = useUser();
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newService, setNewService] = useState({
    name: '',
    description: '',
    category_id: '',
    base_price: '',
    unit: 'hour'
  });

  // ENHANCED: Industry-specific service templates (FIXED: Not contractor-only!)
  const serviceTemplates = {
    'Plumbing': [
      { name: 'Leak Repair', description: 'Fix residential or commercial leaks', base_price: 120, unit: 'service' },
      { name: 'Water Heater Install', description: 'Standard 40-50 gallon tank install', base_price: 1200, unit: 'service' },
      { name: 'Drain Cleaning', description: 'Snaking/jetting services', base_price: 150, unit: 'service' },
      { name: 'Fixture Replacement', description: 'Sinks, faucets, toilets', base_price: 200, unit: 'fixture' },
      { name: 'Emergency Plumbing', description: '24/7 emergency plumbing service', base_price: 180, unit: 'hour' }
    ],
    'Electrical': [
      { name: 'Panel Upgrade', description: 'Electrical panel replacement', base_price: 2500, unit: 'service' },
      { name: 'Outlet Installation', description: 'New outlet or GFCI install', base_price: 150, unit: 'outlet' },
      { name: 'Lighting Repair', description: 'Fix lighting issues', base_price: 100, unit: 'service' },
      { name: 'Ceiling Fan Install', description: 'Ceiling fan installation', base_price: 200, unit: 'service' },
      { name: 'Emergency Electrical', description: '24/7 electrical emergency service', base_price: 200, unit: 'hour' }
    ],
    'HVAC': [
      { name: 'A/C Installation', description: 'Central air conditioning install', base_price: 3500, unit: 'service' },
      { name: 'Furnace Tune-Up', description: 'Annual furnace maintenance', base_price: 200, unit: 'service' },
      { name: 'Duct Cleaning', description: 'Complete ductwork cleaning', base_price: 400, unit: 'service' },
      { name: 'Filter Replacement', description: 'HVAC filter change', base_price: 80, unit: 'service' },
      { name: 'Emergency HVAC', description: '24/7 heating/cooling emergency', base_price: 250, unit: 'hour' }
    ],
    'Landscaping': [
      { name: 'Lawn Mowing', description: 'Weekly lawn maintenance', base_price: 50, unit: 'service' },
      { name: 'Tree Trimming', description: 'Professional tree pruning', base_price: 300, unit: 'service' },
      { name: 'Irrigation Setup', description: 'Sprinkler system install', base_price: 800, unit: 'service' },
      { name: 'Leaf Cleanup', description: 'Seasonal leaf removal', base_price: 120, unit: 'service' },
      { name: 'Landscape Design', description: 'Custom landscape planning', base_price: 1200, unit: 'project' }
    ],
    'Cleaning': [
      { name: 'Deep Clean', description: 'Comprehensive deep cleaning', base_price: 200, unit: 'service' },
      { name: 'Carpet Cleaning', description: 'Professional carpet cleaning', base_price: 150, unit: 'room' },
      { name: 'Move-Out Clean', description: 'Complete move-out cleaning', base_price: 300, unit: 'service' },
      { name: 'Office Cleaning', description: 'Commercial office cleaning', base_price: 100, unit: 'visit' },
      { name: 'Post-Construction', description: 'Construction cleanup service', base_price: 400, unit: 'service' }
    ],
    'General Contracting': [
      { name: 'Kitchen Remodel', description: 'Complete kitchen renovation', base_price: 15000, unit: 'project' },
      { name: 'Bathroom Remodel', description: 'Full bathroom renovation', base_price: 8000, unit: 'project' },
      { name: 'Drywall Repair', description: 'Drywall patching and repair', base_price: 300, unit: 'service' },
      { name: 'Flooring Install', description: 'Hardwood/tile installation', base_price: 8, unit: 'sqft' },
      { name: 'Deck Building', description: 'Custom deck construction', base_price: 5000, unit: 'project' }
    ],
    'Roofing': [
      { name: 'Roof Inspection', description: 'Complete roof assessment', base_price: 200, unit: 'service' },
      { name: 'Shingle Repair', description: 'Shingle replacement service', base_price: 400, unit: 'service' },
      { name: 'Gutter Cleaning', description: 'Gutter cleaning and inspection', base_price: 150, unit: 'service' },
      { name: 'Roof Replacement', description: 'Complete roof replacement', base_price: 12000, unit: 'project' },
      { name: 'Emergency Repair', description: 'Emergency roof repair service', base_price: 500, unit: 'service' }
    ],
    'Pool Service': [
      { name: 'Pool Cleaning', description: 'Weekly pool cleaning and maintenance', base_price: 100, unit: 'service' },
      { name: 'Pool Equipment Repair', description: 'Pump, filter, and heater repairs', base_price: 300, unit: 'repair' },
      { name: 'Pool Opening/Closing', description: 'Seasonal pool opening and closing', base_price: 200, unit: 'service' },
      { name: 'Chemical Balancing', description: 'Water testing and chemical adjustment', base_price: 50, unit: 'service' },
      { name: 'Pool Liner Replacement', description: 'Vinyl liner replacement', base_price: 2500, unit: 'service' }
    ],
    'Pest Control': [
      { name: 'General Pest Treatment', description: 'Comprehensive pest control service', base_price: 150, unit: 'service' },
      { name: 'Termite Inspection', description: 'Professional termite inspection', base_price: 100, unit: 'service' },
      { name: 'Rodent Control', description: 'Mouse and rat control service', base_price: 200, unit: 'service' },
      { name: 'Ant Treatment', description: 'Targeted ant elimination', base_price: 120, unit: 'service' },
      { name: 'Quarterly Service', description: 'Regular pest prevention program', base_price: 80, unit: 'service' }
    ],
    'Garage Door': [
      { name: 'Garage Door Repair', description: 'Spring, opener, and track repairs', base_price: 200, unit: 'repair' },
      { name: 'Garage Door Installation', description: 'New garage door installation', base_price: 800, unit: 'door' },
      { name: 'Opener Installation', description: 'Garage door opener installation', base_price: 300, unit: 'opener' },
      { name: 'Spring Replacement', description: 'Torsion and extension spring replacement', base_price: 150, unit: 'spring' },
      { name: 'Annual Maintenance', description: 'Tune-up and safety inspection', base_price: 100, unit: 'service' }
    ],
    'Locksmith': [
      { name: 'Lock Installation', description: 'Residential and commercial lock installation', base_price: 150, unit: 'lock' },
      { name: 'Emergency Lockout', description: '24/7 emergency lockout service', base_price: 100, unit: 'service' },
      { name: 'Key Duplication', description: 'Key cutting and duplication', base_price: 25, unit: 'key' },
      { name: 'Rekey Service', description: 'Lock rekeying service', base_price: 75, unit: 'lock' },
      { name: 'Security System Install', description: 'Deadbolts and security hardware', base_price: 200, unit: 'system' }
    ],
    'Handyman': [
      { name: 'General Repairs', description: 'Various home repair services', base_price: 100, unit: 'hour' },
      { name: 'Fixture Installation', description: 'Light fixtures, fans, and hardware', base_price: 150, unit: 'fixture' },
      { name: 'Drywall Repair', description: 'Hole patching and drywall repairs', base_price: 200, unit: 'patch' },
      { name: 'Painting Touch-Up', description: 'Small painting and touch-up work', base_price: 120, unit: 'hour' },
      { name: 'Assembly Service', description: 'Furniture and equipment assembly', base_price: 80, unit: 'hour' }
    ]
  };

  const [companyIndustry, setCompanyIndustry] = useState('');

  // The company's OWN service tags (curated AND custom), captured in CompanyBasicsStep.
  // This is the generic path: a business that isn't a trade types what it does
  // ("Bookkeeping", "Lash Extensions"), syncCompanyTags mints a company-scoped custom
  // tag for it, and those names become the service suggestions here. Without this the
  // step could only suggest from the 12 hardcoded trade template sets.
  const [companyServiceTags, setCompanyServiceTags] = useState([]);

  // Map the broad industry slugs stored on companies.industry (set in CompanyBasicsStep's
  // businessCategories) to the serviceTemplates keys (which are specific trade display names).
  // Without this, a slug like "construction" never matches a serviceTemplates key at all.
  // NOTE: these three slugs are broader than any single template set, so each maps to the
  // closest available key. The other 14 categories (professional_services, retail_ecommerce,
  // beauty_personal, education_tutoring, …) are DELIBERATELY absent — they are not trades,
  // and resolveTemplateKey returns null for them so suggestions come from the company's own
  // service tags instead. Do not add them here pointing at a trade library.
  //
  // property_management WAS here, pointing at General Contracting, and it is the same defect
  // this comment warns about — it just took longer to spot because the two sound adjacent. A
  // property manager does not do the work; they HIRE the contractor who does. Mapping them to
  // the GC library greeted them with "Kitchen Remodel $15,000" as a service they offer, and
  // the only way out was to notice the template selector and set it back to None. Their real
  // services are things like tenant turnover, rent collection and inspections, which is
  // exactly what their own service tags already carry.
  const INDUSTRY_SLUG_TO_TEMPLATE_KEY = {
    home_trades: 'General Contracting',
    home_services: 'Cleaning',
    construction: 'General Contracting'
  };

  // Resolve a raw companies.industry value (slug or display name) to a serviceTemplates key,
  // or NULL when it matches no trade template.
  //
  // Returning null is the fix for the SMB fallback bug. This used to return
  // 'General Contracting' for anything unrecognised, and CompanyBasicsStep offers 17
  // business categories against these 12 trade templates — so consulting, legal, retail,
  // beauty, tutoring and 8 others all landed on General Contracting and were shown
  // "Kitchen Remodel $15,000". A null result means "no trade template applies", and the
  // caller falls back to the company's own service tags instead of inventing trade work.
  const resolveTemplateKey = (rawIndustry) => {
    if (!rawIndustry) return null;
    // Already a valid template key (display name)?
    if (serviceTemplates[rawIndustry]) return rawIndustry;
    // Known slug → template key
    if (INDUSTRY_SLUG_TO_TEMPLATE_KEY[rawIndustry]) return INDUSTRY_SLUG_TO_TEMPLATE_KEY[rawIndustry];
    // Case-insensitive display-name match (e.g. "plumbing" → "Plumbing")
    const ciKey = Object.keys(serviceTemplates).find(
      key => key.toLowerCase() === String(rawIndustry).toLowerCase()
    );
    if (ciKey) return ciKey;
    return null;
  };

  // Load existing services and categories
  useEffect(() => {
    loadData();
  }, [user?.company_id]);

  const loadData = async () => {
    if (!user?.company_id) return;

    // Resolved template key for this load pass. Empty string = no trade template applies,
    // which is the normal case for a non-trade SMB.
    let resolvedIndustry = '';

    try {
      setLoading(true);

      // INDUSTRY STANDARD: Load company industry for smart service suggestions
      // FIXED: Graceful fallback for missing columns
      try {
        // Try with industry_tags first, fallback to just name if column doesn't exist
        let companyData;
        let companyError;

        try {
          const result = await supabase
            .from('companies')
            .select('industry_tags, industry, name')
            .eq('id', user.company_id)
            .single();
          companyData = result.data;
          companyError = result.error;
        } catch (err) {
          // If industry_tags column doesn't exist, try without it
          console.log('⚠️ industry_tags column missing, trying fallback query');
          const result = await supabase
            .from('companies')
            .select('industry, name')
            .eq('id', user.company_id)
            .single();
          companyData = result.data;
          companyError = result.error;
        }

        if (companyError) {
          // No trade guess on failure — an unknown business is not a general contractor.
          console.log('⚠️ Company data query failed, no template industry:', companyError.message);
          resolvedIndustry = '';
          setCompanyIndustry(resolvedIndustry);
        } else {
          let detectedIndustry = null;

          // Method 0 (best): the company's own service tags in company_tags.
          //
          // Two jobs here, and they used to be conflated:
          //   (a) DETECTION — find a specific trade template to enrich suggestions with.
          //       Only curated tags can do this, since only they map to template keys.
          //   (b) SUGGESTIONS — keep EVERY service tag name, curated or custom. This is
          //       what a non-trade business has: syncCompanyTags mints company-scoped
          //       custom tags (is_curated=false) for anything typed that isn't curated.
          //
          // This filter used to require is_curated for both, so every custom tag was
          // discarded and the step fell through to the trade fallback. Measured live
          // 2026-08-28: 37 of 59 tagged companies have ONLY custom tags, so detection
          // found nothing for 63% of them.
          try {
            const { data: ctags } = await supabase
              .from('company_tags')
              .select('tags ( name, tag_kind, is_curated )')
              .eq('company_id', user.company_id);
            const serviceTags = (ctags || [])
              .map(r => r.tags)
              .filter(t => t && t.tag_kind === 'service' && t.name);

            setCompanyServiceTags(serviceTags.map(t => t.name));

            for (const t of serviceTags.filter(t => t.is_curated)) {
              const key = resolveTemplateKey(t.name);
              if (key) { detectedIndustry = t.name; break; }
            }
          } catch (e) {
            // company_tags unreachable — fall through to category/name detection.
          }

          // Method 1: smart detection from the company name. Runs BEFORE the
          // business category because categories are broad ("Home Trades" ->
          // General Contracting) and would otherwise mask a specific trade that the
          // name clearly states (e.g. "Sierra Mechanical Heating, Air..." -> HVAC).
          if (!detectedIndustry && companyData?.name) {
            const companyName = companyData.name.toLowerCase();
            const industryKeywords = {
              'HVAC': ['hvac', 'heating', 'cooling', 'air conditioning', 'furnace', 'refrigeration', 'mechanical', ' ac'],
              'Plumbing': ['plumbing', 'plumber', 'pipe', 'drain', 'water', 'sewer'],
              'Electrical': ['electric', 'electrical', 'electrician', 'wiring', 'power'],
              'Roofing': ['roof', 'roofing', 'roofer', 'shingle', 'gutter'],
              'Landscaping': ['landscape', 'landscaping', 'lawn', 'garden', 'tree', 'irrigation'],
              'Pool Service': ['pool', 'spa', 'swimming'],
              'Pest Control': ['pest', 'exterminator', 'bug', 'termite', 'rodent'],
              'Garage Door': ['garage', 'door'],
              'Locksmith': ['lock', 'locksmith', 'key', 'security'],
              'Handyman': ['handyman', 'handymen', 'repair', 'maintenance']
            };
            for (const [industry, keywords] of Object.entries(industryKeywords)) {
              if (keywords.some(keyword => companyName.includes(keyword))) {
                detectedIndustry = industry;
                break;
              }
            }
          }
          // Method 2: the Business Category picked in Step 1 (last resort before generic).
          if (!detectedIndustry && companyData?.industry) {
            detectedIndustry = companyData.industry;
          }

          // Resolve to a serviceTemplates key, or null when this business is not one of
          // the 12 trades. Null is a valid, expected outcome for an SMB — suggestions
          // then come from the company's own service tags rather than a trade guess.
          resolvedIndustry = resolveTemplateKey(detectedIndustry) || '';
          setCompanyIndustry(resolvedIndustry);
          console.log(`🎯 Detected industry: ${detectedIndustry} → resolved template: ${resolvedIndustry || '(none — using company service tags)'}`);
        }
      } catch (error) {
        console.log('⚠️ Company industry detection failed, no template industry');
        resolvedIndustry = '';
        setCompanyIndustry(resolvedIndustry);
      }

      // Load service categories (company-specific and global defaults)
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('service_categories')
        .select('*')
        .or(`company_id.eq.${user.company_id},company_id.is.null`)
        .order('name');

      if (categoriesError) throw categoriesError;

      let categories = categoriesData || [];
      console.log('📊 Loaded categories:', categories.length, categories.map(c => `${c.name} (company_id: ${c.company_id})`));

      // NOTE: We no longer seed per-company `service_categories` rows here.
      // Service categorization is being unified into the tags taxonomy — the
      // company's services/trades are captured as service tags in
      // `company_tags` (tag_kind='service') by CompanyBasicsStep, which is what
      // makes the company matchable in the marketplace. `service_categories`
      // remains only as a legacy FK target for older surfaces (e.g. rate_cards);
      // services created here are scoped by their own service_types.company_id
      // and save with a null category.
      setCategories(categories);

      // Load existing services scoped by service_types.company_id directly.
      // (Previously this used a service_categories!inner join to scope by the
      // category's company_id, which would drop services that have a null
      // category. Services are company-scoped by their own company_id.)
      const { data: servicesData, error: servicesError } = await supabase
        .from('service_types')
        .select(`
          *,
          service_categories ( name )
        `)
        .eq('company_id', user.company_id)
        .order('name');

      if (servicesError) throw servicesError;
      setServices(servicesData || []);

    } catch (error) {
      console.error('Error loading services data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Validate services - INDUSTRY STANDARD: Always allow skip
  const validateServices = () => {
    console.log('🔍 Validating services...');
    console.log('📊 Services count:', services.length);

    // INDUSTRY STANDARD: Always valid - users can skip services entirely
    const isValid = true;
    const errors = [];
    const warnings = [];

    if (services.length === 0) {
      warnings.push('You can add services later for faster quoting');
    } else if (services.length < 3) {
      warnings.push('Consider adding more services to give customers more options');
    }

    console.log('✅ Validation result:', { valid: isValid, errors, warnings });

    onValidationChange?.({
      valid: isValid,
      errors,
      warnings
    });

    return isValid;
  };

  // Validate on services change
  useEffect(() => {
    if (!loading) {
      validateServices();
    }
  }, [services, loading]);

  // Add service template
  const addServiceTemplate = async (template) => {
    if (!user?.company_id) return;

    try {
      // category_id is intentionally null — service categorization is unified
      // into the tags taxonomy; services are scoped by company_id.
      // A tag-derived suggestion has no price, so it gets no duration either. Deriving
      // duration from price only makes sense for the trade templates, where the price is
      // ours and the work is known — inferring "2 hours" from a blank price would invent
      // a fact about a business we know nothing about.
      const hasPrice = template.base_price !== null && template.base_price !== undefined;

      const serviceData = {
        company_id: user.company_id,
        category_id: null,
        name: template.name,
        description: template.description,
        base_price: hasPrice ? template.base_price : null,
        estimated_duration: hasPrice ? (template.base_price > 1000 ? '1 day' : '2 hours') : null,
        is_active: true
      };

      const { data, error } = await supabase
        .from('service_types')
        .insert(serviceData)
        .select(`
          *,
          service_categories (name)
        `)
        .single();

      if (error) throw error;

      setServices(prev => [...prev, data]);
    } catch (error) {
      console.error('Error adding service template:', error);
    }
  };

  // Add custom service
  const addCustomService = async () => {
    if (!newService.name.trim() || !user?.company_id) return;

    try {
      setSaving(true);

      const serviceData = {
        company_id: user.company_id,
        category_id: null,
        name: newService.name.trim(),
        description: newService.description.trim(),
        base_price: parseFloat(newService.base_price) || 0,
        estimated_duration: '2 hours', // Default duration
        is_active: true
      };

      const { data, error } = await supabase
        .from('service_types')
        .insert(serviceData)
        .select(`
          *,
          service_categories (name)
        `)
        .single();

      if (error) throw error;

      setServices(prev => [...prev, data]);
      setNewService({
        name: '',
        description: '',
        category_id: '',
        base_price: '',
        unit: 'hour'
      });

    } catch (error) {
      console.error('Error adding custom service:', error);
    } finally {
      setSaving(false);
    }
  };

  // Remove service
  const removeService = async (serviceId) => {
    try {
      const { error } = await supabase
        .from('service_types')
        .delete()
        .eq('id', serviceId);

      if (error) throw error;

      setServices(prev => prev.filter(s => s.id !== serviceId));
    } catch (error) {
      console.error('Error removing service:', error);
    }
  };

  // Build the suggestion tiles.
  //
  // Two sources, in priority order, deduplicated by name (case-insensitive):
  //   1. A trade template set, when one genuinely applies (detected from a curated tag,
  //      or picked explicitly from the industry dropdown). These carry a description,
  //      a price and a unit.
  //   2. The company's OWN service tags — whatever the owner typed to describe their
  //      business. Offered as bare services with NO price, because we don't know what a
  //      bookkeeper charges and a made-up number is worse than a blank field.
  //
  // If neither yields anything the caller renders an empty state. There is deliberately
  // no generic fallback: suggesting trade work to a law firm is the bug this replaces.
  const getRelevantTemplates = () => {
    const out = [];
    const seen = new Set();

    // Tag names are stored however they were captured — curated ones are lowercase or
    // snake_case ('general_contractor', 'web design'), typed ones keep the user's casing
    // ('Faucet Repair'). A tag becomes a customer-visible SERVICE NAME here, so tidy the
    // machine-shaped ones. Only touch names that are all-lowercase or contain an
    // underscore: uppercasing every word would turn 'HVAC' into 'Hvac'.
    const humanizeTagName = (raw) => {
      const name = String(raw || '').trim();
      if (!name) return name;
      if (!name.includes('_') && name !== name.toLowerCase()) return name;
      return name
        .replace(/_/g, ' ')
        .replace(/\s+/g, ' ')
        .split(' ')
        .map(w => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
        .join(' ');
    };

    const push = (item) => {
      const key = (item.name || '').trim().toLowerCase();
      if (!key || seen.has(key)) return;
      seen.add(key);
      out.push(item);
    };

    // 1. Trade templates, only when a specific trade actually applies.
    const industryKey = companyIndustry
      ? (serviceTemplates[companyIndustry] ? companyIndustry : resolveTemplateKey(companyIndustry))
      : null;
    if (industryKey && serviceTemplates[industryKey]) {
      serviceTemplates[industryKey].forEach(push);
    }

    // 2. The company's own service tags.
    companyServiceTags.forEach(tagName => {
      const tagKey = resolveTemplateKey(tagName);
      if (tagKey && serviceTemplates[tagKey]) {
        // The tag names a trade we have real templates for — offer the richer version.
        serviceTemplates[tagKey].forEach(push);
      } else {
        // Anything else: the tag itself is the service. base_price null, not 0 — 0 is a
        // price the user never set and would flow into quotes as a free service.
        push({ name: humanizeTagName(tagName), description: '', base_price: null, unit: 'service' });
      }
    });

    return out;
  };

  const handleComplete = () => {
    console.log('🔄 ServicesStep handleComplete called');
    console.log('📊 Current services:', services);
    console.log('📊 Current categories:', categories);

    const isValid = validateServices();
    console.log('✅ Validation result:', isValid);

    if (isValid) {
      console.log('🚀 Calling onComplete with services data');
      onComplete?.({ services });
    } else {
      console.log('❌ Validation failed, not proceeding');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12 [color-scheme:dark]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading services...</p>
      </div>
    );
  }

  const industryTemplates = getRelevantTemplates();
  const allIndustries = Object.keys(serviceTemplates);

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <WrenchScrewdriverIcon className="w-12 h-12 text-green-600 mx-auto mb-4" />
        {/* This header sits directly on the always-dark bg-slate-900 wizard shell (outside
            any card), so use light text — text-gray-900 here was dark-on-dark. */}
        <h2 className="text-2xl font-bold text-white mb-2">Services & Pricing</h2>
        <p className="text-slate-400">
          Define the services you provide. This helps create accurate quotes and lets customers know what you offer.
        </p>
      </div>

      {/* Service Templates — industry dropdown above grid replaces the
          previous "Browse Other Service Types" pill row (TKT-0004). Pills
          were visually indistinguishable from service tiles and the click
          silently replaced the visible grid. Dropdown is unambiguous: the
          control is clearly a selector, not a tile, and switching is an
          explicit two-step interaction (open → choose). */}
      {industryTemplates.length > 0 && (
        <div className="bg-gradient-to-br from-white/80 dark:from-slate-900/80 to-blue-50/40 dark:to-blue-900/20 backdrop-blur-xl rounded-lg shadow-2xl border border-blue-200/50 dark:border-blue-900/50-sm border p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              ⚡ Quick Start Services
            </h3>
            {allIndustries.length > 1 && (
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <span>Also show templates for:</span>
                <select
                  value={companyIndustry || ''}
                  onChange={(e) => setCompanyIndustry(e.target.value)}
                  className="px-3 py-1 text-sm border rounded-md bg-white hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 [color-scheme:dark]"
                >
                  {/* A blank option is required: these are the 12 TRADE libraries, and a
                      business that isn't a trade must be able to select none of them.
                      Without it the select would show "Plumbing" to an accountant purely
                      because it was first in the list. */}
                  <option className={selectOptionClass} value="">None — just my services</option>
                  {allIndustries.map(industry => (
                    <option className={selectOptionClass} key={industry} value={industry}>{industry}</option>
                  ))}
                </select>
              </label>
            )}
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Click a tile to add (or remove) it. Already-added services keep your selections even if you switch industries above.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {industryTemplates.map((template, index) => {
              const existing = services.find(s => s.name === template.name);
              const isAdded = Boolean(existing);
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => {
                    if (isAdded) {
                      removeService(existing.id);
                    } else {
                      addServiceTemplate(template);
                    }
                  }}
                  className={`text-left p-3 border rounded-md transition-colors ${
                    isAdded
                      ? 'border-green-400 bg-green-50 text-green-800 hover:border-red-300 hover:bg-red-50 dark:bg-green-500/15 dark:border-green-500/50 dark:text-green-200 dark:hover:bg-red-500/15 dark:hover:border-red-500/50'
                      : 'border-gray-200 hover:border-green-300 hover:bg-green-50 dark:border-slate-700 dark:hover:border-green-500/50 dark:hover:bg-green-500/10'
                  }`}
                  title={isAdded ? 'Click to remove' : 'Click to add'}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-gray-900 dark:text-white">{template.name}</div>
                    {isAdded && <CheckCircleIcon className="w-5 h-5 text-green-600" />}
                  </div>
                  {template.description && (
                    <div className="text-sm text-gray-600 dark:text-slate-300">{template.description}</div>
                  )}
                  {template.base_price !== null && template.base_price !== undefined ? (
                    <div className="text-sm text-green-600 dark:text-green-400 mt-1">
                      <CurrencyDollarIcon className="w-4 h-4 inline mr-1" />
                      ${template.base_price} per {template.unit}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 dark:text-slate-400 mt-1">Set your price after adding</div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty state — no trade template applies AND the company has no service tags to
          suggest from. Previously this case rendered the General Contracting library, so a
          consultant was offered Kitchen Remodel. Showing nothing is correct; showing a way
          forward is required, hence the CTA and the trade-library picker (a trade that
          simply skipped the tags step still needs to reach its templates). */}
      {industryTemplates.length === 0 && (
        <div className="bg-gradient-to-br from-white/80 dark:from-slate-900/80 to-blue-50/40 dark:to-blue-900/20 backdrop-blur-xl rounded-lg shadow-2xl border border-blue-200/50 dark:border-blue-900/50-sm border p-6 mb-6 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Add the services you offer
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Add your first service below — or skip this step and set them up later.
            Anything you add here becomes reusable on quotes and invoices.
          </p>
          {allIndustries.length > 0 && (
            <label className="inline-flex items-center gap-2 text-sm text-gray-600">
              <span>Work in a trade? Start from a template:</span>
              <select
                value={companyIndustry || ''}
                onChange={(e) => setCompanyIndustry(e.target.value)}
                className="px-3 py-1 text-sm border rounded-md bg-white hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 [color-scheme:dark]"
              >
                <option className={selectOptionClass} value="">Choose a trade…</option>
                {allIndustries.map(industry => (
                  <option className={selectOptionClass} key={industry} value={industry}>{industry}</option>
                ))}
              </select>
            </label>
          )}
        </div>
      )}

      {/* Current Services */}
      {services.length > 0 && (
        <div className="bg-gradient-to-br from-white/80 dark:from-slate-900/80 to-blue-50/40 dark:to-blue-900/20 backdrop-blur-xl rounded-lg shadow-2xl border border-blue-200/50 dark:border-blue-900/50-sm border p-6 mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Your Services</h3>
          <div className="space-y-3">
            {services.map((service) => (
              <div key={service.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-md">
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{service.name}</div>
                  {service.description && (
                    <div className="text-sm text-gray-600">{service.description}</div>
                  )}
                  {service.base_price !== null && service.base_price !== undefined ? (
                    <div className="text-sm text-green-600 mt-1">
                      <CurrencyDollarIcon className="w-4 h-4 inline mr-1" />
                      ${service.base_price}{service.unit ? ` per ${service.unit}` : ''}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 mt-1">No price set</div>
                  )}
                </div>
                <button
                  onClick={() => removeService(service.id)}
                  className="text-red-600 hover:text-red-800 p-1"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Custom Service */}
      <div className="bg-gradient-to-br from-white/80 dark:from-slate-900/80 to-blue-50/40 dark:to-blue-900/20 backdrop-blur-xl rounded-lg shadow-2xl border border-blue-200/50 dark:border-blue-900/50-sm border p-6 mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Add Custom Service</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Service Name *
            </label>
            <input
              type="text"
              value={newService.name}
              onChange={(e) => setNewService(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="e.g., Drain Cleaning"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={newService.description}
              onChange={(e) => setNewService(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 focus:outline-none focus:ring-2 focus:ring-green-500"
              rows="2"
              placeholder="Brief description of the service"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Base Price
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 sm:text-sm">$</span>
              </div>
              <input
                type="number"
                value={newService.base_price}
                onChange={(e) => setNewService(prev => ({ ...prev, base_price: e.target.value }))}
                className="w-full pl-7 pr-3 py-2 border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Unit
            </label>
            <select
              value={newService.unit}
              onChange={(e) => setNewService(prev => ({ ...prev, unit: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 focus:outline-none focus:ring-2 focus:ring-green-500 [color-scheme:dark]"
            >
              <option className={selectOptionClass} value="hour">Per Hour</option>
              <option className={selectOptionClass} value="service">Per Service</option>
              <option className={selectOptionClass} value="project">Per Project</option>
              <option className={selectOptionClass} value="sqft">Per Sq Ft</option>
              <option className={selectOptionClass} value="item">Per Item</option>
              <option className={selectOptionClass} value="day">Per Day</option>
            </select>
          </div>
        </div>

        <div className="mt-4">
          <button
            onClick={addCustomService}
            disabled={!newService.name.trim() || saving}
            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white font-medium rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Adding...
              </>
            ) : (
              <>
                <PlusIcon className="w-4 h-4" />
                Add Service
              </>
            )}
          </button>
        </div>
      </div>

      {/* Sticky Continue area (TKT-0004): keep visible as "Your Services"
          and the custom-service form expand the page. Previously this slid
          out of viewport once the user added services, leading to the
          "Continue with X Services button popping in and out" complaint. */}
      <div className="sticky bottom-4 z-10 mt-6 text-center">
        <div className="inline-flex flex-col items-center gap-2 bg-white/85 dark:bg-slate-900/85 dark:border dark:border-slate-700/60 backdrop-blur-sm rounded-lg shadow-md px-6 py-3">
          <button
            type="button"
            onClick={handleComplete}
            className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white font-medium rounded-md hover:bg-green-700"
          >
            <CheckCircleIcon className="w-5 h-5" />
            {services.length === 0
              // Names what is actually skipped. The old copy mentioned only pricing, but this
              // step builds the SERVICE CATALOGUE too -- skipping leaves both empty, and someone
              // reading "I'll quote jobs individually" would not expect to find no services.
              ? 'Skip - no service list, quote each job individually'
              : `Continue with ${services.length} Service${services.length !== 1 ? 's' : ''}`
            }
          </button>

          {services.length > 0 && (
            <div className="text-sm text-gray-500 dark:text-slate-400">
              You can always add more services later from Settings
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServicesStep;


